import React, { useState, useRef, useEffect, useCallback } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleMapsLoader } from "../../../GoogleMapsProvider";

const containerStyle = { width: "100%", height: "300px" };

/**
 * Props:
 *  coordinates         – { lat, lng } controlled by parent
 *  onDragEnd           – (event) => void
 *  onReverseGeocode    – (formattedAddress: string) => void  ← parent sets its address field ONLY
 *  onPlaceSelect       – ({ lat, lng }) => void              ← parent updates coordinates
 *  onLocationConfirmed – (isConfirmed: boolean) => void
 *  showMap             – boolean
 *
 * The landmark <input> inside this component is 100% self-contained local state.
 * It NEVER reads from or writes to any parent address field.
 */
const MapWithConditionalRendering = ({
  coordinates,
  onDragEnd,
  showMap,
  onReverseGeocode,
  onPlaceSelect,
  onLocationConfirmed,
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  // landmark is LOCAL ONLY — it never syncs to the parent address field
  const [landmark, setLandmark]   = useState("");
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);

  const mapRef          = useRef(null);
  const markerRef       = useRef(null);
  const geocoderRef     = useRef(null);
  const autocompleteRef = useRef(null);
  const inputRef        = useRef(null);
  const hasCenteredRef  = useRef(false);

  const { isLoaded, loadError } = useGoogleMapsLoader();

  // ── Helper: reverse geocode → send formatted address string to parent ─────
  const reverseGeocode = useCallback(
    (lat, lng) => {
      if (!geocoderRef.current) return;
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.length > 0) {
          // Pass only the string — parent owns what it does with it
          if (onReverseGeocode) onReverseGeocode(results[0].formatted_address);
        }
      });
    },
    [onReverseGeocode]
  );

  // ── 1. Init Geocoder ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoaded && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  // ── 2. Init Places Autocomplete on the landmark input ────────────────────
  // Using the legacy google.maps.places.Autocomplete constructor. The newer
  // PlaceAutocompleteElement requires the separate "Places API (New)" to be
  // enabled/billed on the Google Cloud project, which caused 403 errors.
  // The legacy Autocomplete only needs the classic Places API (already
  // enabled and working), so it's used directly here.
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    const onPlaceResolved = ({ lat, lng, label }) => {
      setUsingCurrentLocation(false);
      setLandmark(label || "");
      if (onPlaceSelect) onPlaceSelect({ lat, lng });
      reverseGeocode(lat, lng);
      if (mapRef.current) {
        mapRef.current.panTo({ lat, lng });
        mapRef.current.setZoom(17);
      }
      if (onLocationConfirmed) onLocationConfirmed(true);
    };

    const legacyAutocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: "pk" },
        types: ["establishment", "geocode"],
        fields: ["geometry", "formatted_address", "name"],
      }
    );

    legacyAutocomplete.addListener("place_changed", () => {
      const place = legacyAutocomplete.getPlace();
      if (!place?.geometry?.location) return;
      onPlaceResolved({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        label: place.name || place.formatted_address || "",
      });
    });
    autocompleteRef.current = legacyAutocomplete;
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Reset mapLoaded when map toggled ───────────────────────────────────
  useEffect(() => {
    if (showMap && !mapRef.current) setMapLoaded(false);
  }, [showMap]);

  // ── 4. Pan once when coordinates first arrive ─────────────────────────────
  useEffect(() => {
    if (
      mapRef.current &&
      coordinates?.lat &&
      coordinates?.lng &&
      !hasCenteredRef.current
    ) {
      mapRef.current.panTo({ lat: coordinates.lat, lng: coordinates.lng });
      mapRef.current.setZoom(16);
      hasCenteredRef.current = true;
    }
  }, [coordinates]);

  // ── 5. Map load callback ──────────────────────────────────────────────────
  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (coordinates?.lat && coordinates?.lng && !hasCenteredRef.current) {
      map.panTo({ lat: coordinates.lat, lng: coordinates.lng });
      map.setZoom(16);
      hasCenteredRef.current = true;
    }
    setTimeout(() => setMapLoaded(true), 300);
  };

  // ── 6. Marker drag ────────────────────────────────────────────────────────
  const handleDragEnd = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      onDragEnd(event);           // parent stores coordinates
      reverseGeocode(lat, lng);   // parent gets updated address string

      if (onLocationConfirmed) onLocationConfirmed(true);
    },
    [onDragEnd, reverseGeocode, onLocationConfirmed]
  );

  // ── 7. GPS button ─────────────────────────────────────────────────────────
  const handleGPS = () => {
    if (!navigator.geolocation) return;
    hasCenteredRef.current = false;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        // Move marker in parent
        if (onPlaceSelect) onPlaceSelect({ lat, lng });

        // Send resolved address to parent address field
        reverseGeocode(lat, lng);

        // Pan map
        if (mapRef.current) {
          mapRef.current.panTo({ lat, lng });
          mapRef.current.setZoom(16);
          hasCenteredRef.current = true;
        }

        // Only the landmark label gets "Current Location" — the address field is set by reverseGeocode
        setLandmark("📍 Current Location");
        setUsingCurrentLocation(true);

        if (onLocationConfirmed) onLocationConfirmed(true);
      },
      (err) => {
        console.error("GPS error:", err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (loadError) return <div className="p-4 text-red-500">Error loading map</div>;
  if (!isLoaded) return <div className="p-4 text-gray-400">Loading Map...</div>;

  return (
    <div className="space-y-2">

      {/* ── Landmark / Nearest Place Search ── */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wide">
          Nearest Landmark or Place
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </span>

          <input
            ref={inputRef}
            type="text"
            className="border border-gray-300 rounded-md w-full pl-9 pr-10 py-2 text-sm
                       focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="e.g. Sunar Hotel, Jodia Bazar, Civil Hospital…"
            value={landmark}
            onChange={(e) => {
              setLandmark(e.target.value);
              setUsingCurrentLocation(false);
            }}
            autoComplete="off"
          />

          {/* GPS — sets coordinates + resolves address string; landmark label = "Current Location" */}
          <button
            type="button"
            title="Use my current location"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-primary hover:text-red-600 transition-colors"
            onClick={handleGPS}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10
                       10-4.48 10-10S17.52 2 12 2zm1 17.93V18h-2v1.93
                       C7.06 19.48 4.52 16.94 4.07 13H6v-2H4.07
                       C4.52 7.06 7.06 4.52 11 4.07V6h2V4.07
                       c3.94.45 6.48 2.99 6.93 6.93H18v2h1.93
                       c-.45 3.94-2.99 6.48-6.93 6.93z"/>
            </svg>
          </button>
        </div>
        {usingCurrentLocation ? (
          <p className="text-xs text-primary mt-1">📍 Using current location</p>
        ) : (
          <p className="text-xs text-gray-400 mt-1">
            Search a nearby place <strong>or</strong> drag the red pin on the map to set your exact location.
          </p>
        )}
      </div>

      {/* ── Google Map ── */}
      {showMap ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={
            coordinates?.lat && coordinates?.lng
              ? coordinates
              : { lat: 24.8607, lng: 67.0011 }
          }
          zoom={13}
          onLoad={handleMapLoad}
          options={{ streetViewControl: false, mapTypeControl: false }}
        >
          {mapLoaded && coordinates?.lat && coordinates?.lng && (
            <Marker
              position={coordinates}
              draggable
              onLoad={(m) => { markerRef.current = m; }}
              onDragEnd={handleDragEnd}
            />
          )}
        </GoogleMap>
      ) : (
        <div className="h-[300px] bg-gray-100 flex items-center justify-center text-sm text-gray-400">
          Map is hidden
        </div>
      )}
    </div>
  );
};

export default MapWithConditionalRendering;